import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Flex, Form, Input, Tag } from 'antd';
import GooglePlacesSearch from '../../form/address/GooglePlacesSearch';
import useApiClient from '../../../hooks/api/useApiClient';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import { useSaveServiceContractMutation } from '../../../store/api-slices';
import { SignatureProvider } from '../../../services/exports/Constants';

const { TextArea } = Input;

/**
 * Step 2 — recipient details for the contract (name, email, additional
 * locations, special agreements). The details are persisted to the service
 * contract when advancing, so they survive a refresh before previewing.
 * Advances to the preview step once the required fields are filled and saved.
 */
export default function YousignCreatePreviewStep({
  data,
  update,
  isSkippable,
  skip,
  skipping,
  onBack,
  onNext,
  onPersist,
  isPersisting,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:YousignContractFlow' });
  const { t: tSign } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  const { ServiceContractsManager } = useApiClient();
  const [saveContract, { isLoading: saving }] = useSaveServiceContractMutation();

  const [locationSearch, setLocationSearch] = useState('');
  // Every field this step posts has a `form.<field>.label` key in the sign-step
  // namespace, so `FeedbackLabel` can name the fields the backend rejected.
  const { setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel({ t: tSign });

  const recipientComplete =
    Boolean(data.signer_first_name?.trim()) &&
    Boolean(data.signer_last_name?.trim()) &&
    Boolean(data.recipient_email?.trim());

  const addAdditionalLocation = (place) => {
    const name = place?.name?.split(',')[0] ?? place?.name;
    const address = place?.formatted_address ?? null;
    const placeId = place?.place_id ?? null;

    if (!name || !address || !placeId) {
      return;
    }

    const exists = data.additional_locations.some(
      (location) =>
        (placeId && location.place_id === placeId) || (location.name === name && location.address === address),
    );

    if (!exists) {
      update('additional_locations', [...data.additional_locations, { name, address, place_id: placeId }]);
    }

    setLocationSearch('');
  };

  const removeAdditionalLocation = (index) =>
    update(
      'additional_locations',
      data.additional_locations.filter((_, i) => i !== index),
    );

  const saveAndContinue = async () => {
    // Persistence override (switch-to-annual wizard) stages only this step's
    // recipient fields into `pending_changes` instead of committing the contract.
    // The parent owns the staged payload; this step just triggers it.
    if (onPersist) {
      await onPersist();

      return;
    }

    resetFeedback();

    const { error } = await saveContract({
      apiManager: ServiceContractsManager,
      data: {
        ...data,
        signature_provider: SignatureProvider.Yousign,
      },
    });

    if (error) {
      // `baseQuery` passes the raw backend body through as `error`, so this is
      // the `{ message, errors }` validation payload.
      setErrorFeedback(error);

      return toast.error(t('toasts.failed_to_save_changes'));
    }

    onNext();
  };

  return (
    <div className="mt-medium">
      <h2 className="mb-2">{t('create_preview.header')}</h2>
      <p className="mb-4">{t('create_preview.description')}</p>
      {renderFeedbackLabel}
      <Form layout="vertical" className="mt-medium">
        <Form.Item label={tSign('form.signer_first_name.label')}>
          <Input
            value={data.signer_first_name}
            id="signer_first_name"
            onChange={(e) => update('signer_first_name', e.target.value)}
            placeholder={tSign('form.signer_first_name.placeholder')}
            className="w-3/5"
          />
        </Form.Item>
        <Form.Item label={tSign('form.signer_last_name.label')}>
          <Input
            value={data.signer_last_name}
            id="signer_last_name"
            onChange={(e) => update('signer_last_name', e.target.value)}
            placeholder={tSign('form.signer_last_name.placeholder')}
            className="w-3/5"
          />
        </Form.Item>
        <Form.Item label={tSign('form.recipient_email.label')}>
          <Input
            type="email"
            value={data.recipient_email}
            id="recipient_email"
            onChange={(e) => update('recipient_email', e.target.value)}
            placeholder={tSign('form.recipient_email.placeholder')}
            className="w-3/5"
          />
        </Form.Item>
        <Form.Item label={tSign('form.additional_locations.label')}>
          <div className="w-3/5">
            <GooglePlacesSearch
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
              onPlaceSelected={addAdditionalLocation}
              placeholder={tSign('form.additional_locations.placeholder')}
              options={{
                fields: ['name', 'formatted_address', 'place_id'],
                types: ['bakery', 'bar', 'cafe', 'restaurant', 'food'],
              }}
            />
          </div>
          {data.additional_locations.length > 0 && (
            <Flex wrap gap="small" className="mt-mini">
              {data.additional_locations.map((location, index) => (
                <Tag
                  key={location.place_id ?? `${location.name}-${location.address}`}
                  closable
                  onClose={(e) => {
                    e.preventDefault();
                    removeAdditionalLocation(index);
                  }}
                  className="m-0"
                >
                  {location.address ? `${location.name}, ${location.address}` : location.name}
                </Tag>
              ))}
            </Flex>
          )}
        </Form.Item>
        <Form.Item label={tSign('form.special_agreements.label')}>
          <TextArea
            value={data.special_agreements}
            id="special_agreements"
            onChange={(e) => update('special_agreements', e.target.value)}
            placeholder={tSign('form.special_agreements.placeholder')}
            autoSize={{ minRows: 3, maxRows: 6 }}
            className="w-3/5"
          />
        </Form.Item>
      </Form>
      <Flex justify="space-between" gap="small">
        {isSkippable && (
          <Button color="default" variant="solid" onClick={skip} loading={skipping}>
            {t('buttons.skip')}
          </Button>
        )}
        <Flex justify="end" gap="small" className="ml-auto">
          <Button onClick={onBack}>{tSign('buttons.back')}</Button>
          <Button
            type="primary"
            disabled={!recipientComplete}
            loading={onPersist ? isPersisting : saving}
            onClick={saveAndContinue}
          >
            {t('buttons.next')}
          </Button>
        </Flex>
      </Flex>
    </div>
  );
}
